import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe, Check } from "lucide-react";
import { MASAR } from "@/constants/testIds";
import BrandMark from "@/components/masar/BrandMark";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";

const APPSTORE = "https://apps.apple.com/us/app/masar-community/id6787036272";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const links = [
    { id: "how-it-works", label: t("nav.howItWorks"), testid: MASAR.navHowItWorks },
    { id: "services", label: t("nav.services"), testid: MASAR.navServices },
    { id: "coverage", label: t("nav.coverage"), testid: MASAR.navCoverage },
    { id: "faq", label: t("nav.faq"), testid: MASAR.navFaq },
    { id: "support", label: t("nav.support"), testid: MASAR.navSupport },
    { id: "privacy", label: t("nav.privacy"), testid: "masar-nav-privacy" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`sticky top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/85 border-b border-white/10"
          : "bg-black/60 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button
          data-testid={MASAR.navLogo}
          onClick={() => scrollTo("top")}
          className="flex items-center gap-2.5 group"
        >
          <BrandMark size={40} />
          <span className="font-semibold text-lg tracking-tight text-white">MASAR</span>
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-neutral-400 border border-white/15 px-1.5 py-0.5 rounded">
            {t("nav.tag")}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollTo(l.id)}
              className="px-3 py-2 text-sm text-neutral-300 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#FF6A1A] origin-left scale-x-0 group-hover:scale-x-100 transition-transform" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              data-testid="masar-lang-switcher"
              onClick={() => setLangOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-neutral-200 hover:text-white hover:border-white/30 transition-colors text-xs font-mono uppercase tracking-widest"
              aria-label="Change language"
            >
              <Globe size={14} />
              {LANGUAGES.find((l) => l.code === lang)?.label}
            </button>
            {langOpen && (
              <>
                <button
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setLangOpen(false)}
                  aria-label="Close language menu"
                  tabIndex={-1}
                />
                <div
                  data-testid="masar-lang-menu"
                  className="absolute right-0 top-11 z-50 min-w-36 rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur-xl p-1.5 shadow-2xl"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`masar-lang-${l.code}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                        lang === l.code
                          ? "text-white bg-white/10"
                          : "text-neutral-400 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <span>{l.name}</span>
                      {lang === l.code && <Check size={14} className="text-[#FF6A1A]" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Button
            data-testid={MASAR.navDownloadCta}
            onClick={() => window.open(APPSTORE, "_blank", "noopener,noreferrer")}
            className="hidden md:inline-flex rounded-full bg-[#FF6A1A] text-white hover:bg-[#ff7d38] h-9 px-5 text-sm"
          >
            {t("nav.getApp")}
          </Button>
          <button
            data-testid={MASAR.mobileMenuToggle}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <nav className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => scrollTo(l.id)}
                className="text-left px-2 py-3 text-neutral-200 border-b border-white/5 last:border-0"
              >
                {l.label}
              </button>
            ))}
            <div className="flex flex-wrap items-center gap-2 px-2 pt-4">
              <Globe size={14} className="text-neutral-400" />
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  data-testid={`masar-lang-${l.code}-mobile`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    lang === l.code
                      ? "border-[#FF6A1A] text-white bg-[#FF6A1A]/15"
                      : "border-white/15 text-neutral-400 hover:text-white"
                  }`}
                >
                  {l.name}
                </button>
              ))}
            </div>
            <Button
              data-testid="masar-nav-download-cta-mobile"
              onClick={() => window.open(APPSTORE, "_blank", "noopener,noreferrer")}
              className="mt-3 rounded-full bg-[#FF6A1A] text-white h-11"
            >
              {t("nav.getApp")}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
